/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRef } from 'react';
import { Outlet, useMatches } from 'react-router';
import type { Route } from './+types/_app';
import { usePageUIConfig, mainPaddingDataAttributes } from '@/lib/routes/page-ui-config';
import { siteContext, type SiteContext } from '@salesforce/storefront-next-runtime/site-context';
import { getLogger } from '@/lib/logger.server';
import Header from '@/components/header';
import SiteTicker from '@/components/site-ticker';
import Footer from '@/components/footer';
import { NavContentProvider, CuratedDesktopNav, CuratedMobileNav } from '@/components/navigation-menu-curated';
import { resolveNavContentAdapter, type NavDocument } from '@/lib/adapters/nav-content';
import { useTranslation } from 'react-i18next';
import { WishlistMergeToast } from '@/components/wishlist/wishlist-merge-toast';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import {
    fetchComponentWithComponentData,
    type ComponentWithComponentData,
} from '@/lib/page-designer/component-loader.server';
import { useBrand } from '@/lib/brand/use-brand';

type LoaderData = {
    navDocument: NavDocument;
    headerComponent: Promise<ComponentWithComponentData | null>;
};

/**
 * The nav document is static seed data (and later, adapter-served content
 * that changes per deploy, not per navigation) — one load per session is
 * enough, exactly as the old category-tree fetch behaved.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
export function shouldRevalidate() {
    return false;
}

/**
 * Header navigation is served by the nav-content adapter (curated, merchandised
 * silos captured from production — src/lib/adapters/nav-content/), NOT by the
 * raw catalog category tree. The previous implementation fetched the SCAPI
 * category tree here; the curated document replaces those calls entirely
 * (decision record: docs/nm-migration-handoff.md "Nav-content adapter +
 * merchandised nav seed").
 */
export async function loader({ context, request }: Route.LoaderArgs): Promise<LoaderData> {
    const logger = getLogger(context);

    // Site-aware document selection: the URL alias wins over the raw id because
    // the BergdorfGoodman entry's underlying site id is still NeimanMarcus while
    // SLAS blocks BG channel minting (same convention as home-content).
    const siteCtx = context.get(siteContext) as SiteContext;
    const siteRef = siteCtx.site.alias ?? siteCtx.site.id;
    logger.debug('AppLayout: loader starting', { siteRef });

    const navDocument = await resolveNavContentAdapter(siteRef).getNavDocument();

    // Fetch header embedded component data (non-blocking, streamed to client)
    const headerComponentPromise = fetchComponentWithComponentData(
        { context, request, params: {} } as Route.LoaderArgs,
        { componentId: 'header' }
    );

    return {
        navDocument,
        headerComponent: headerComponentPromise,
    };
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Header with the curated navigation (desktop silo row + mobile menu, both
 *   consuming the same NavDocument through NavContentProvider)
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * For routes without default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({ loaderData: { navDocument, headerComponent } }: { loaderData: LoaderData }) {
    const { t } = useTranslation('header');
    const brand = useBrand();
    const refHeaderComponent = useRef<Promise<ComponentWithComponentData | null> | undefined>(undefined);
    if (!refHeaderComponent.current) {
        refHeaderComponent.current = headerComponent;
    }

    // Reflect the route's `handle.ui` config onto <main> as data-* attributes
    // during render, so the correct top padding is present in the SSR'd HTML.
    // If your CSS keys <main> padding off these, emitting them at render (not
    // in a post-hydration effect) avoids a layout shift (CLS) when the padding
    // would otherwise be added after first paint. Inert if no CSS matches.
    const pageUIConfig = usePageUIConfig();
    const mainPaddingAttrs = mainPaddingDataAttributes(pageUIConfig);

    // Whether the ticker band exists on this page AT ALL. Same `handle.ui`
    // mechanism as the padding above and read from the same object — a route
    // that declares nothing keeps the band, so this is inert everywhere but the
    // one route that opts out. The evidence for that opt-out sits on
    // `PageUIConfig.ticker.visible`, not here, so the route and the shell cite
    // one place rather than two.
    const showTicker = pageUIConfig.ticker?.visible !== false;

    // Route-aware desktop ticker band (owner, 2026-08-16: live drift 50 home /
    // 44 elsewhere — matched per page for flicker parity on search). The
    // layout is the one place that knows the route, so it stamps the variant;
    // the height itself stays token-driven (tokens/header.css --ticker-height
    // + the [data-ticker-band='home'] rule). Mobile stays 60 everywhere.
    const matches = useMatches();
    const isHomeRoute = matches.some((m) => m.id === 'routes/_app._index');

    // <WishlistMergeToast> stays at the app shell — it reads URL params and a one-time
    // cookie set by the post-login redirect target, not wishlist state. Routes that need
    // wishlist hooks mount their own <DeferredWishlistProvider> so the SCAPI call only
    // fires on pages that actually render wishlist UI.
    return (
        <>
            <WishlistMergeToast />
            {/* SiteTicker lives ABOVE <header>, not inside it — matches production DOM structure.
                Copy is capture-verbatim per brand (site-preference / Page Designer wiring later):
                BG from the 2026-08-15 bergdorfgoodman.com capture, NM's five slides from the
                2026-08-15 neimanmarcus.com capture (.reference/homepage.desktop.html). Text-only —
                production slides link to prod NM category pages, which would navigate the demo away. */}
            {showTicker && (
                <SiteTicker
                    band={isHomeRoute ? 'home' : 'default'}
                    messages={
                        brand === 'bg'
                            ? [
                                  /* BG'S FOUR SLIDES, VERBATIM AND IN LIVE'S ORDER
                                     (.reference/captures/bg-search-m-375/App.tsx, the
                                     slide track at the head of the document — the same
                                     four in the same order in bg-home-m-375 and
                                     bg-plp-m-440). They were `bgPlaceholder1/2` and are
                                     named `bg1..bg4` now because they are measured copy,
                                     not placeholders; no locale file carried the old
                                     keys, so nothing else referenced them.

                                     THE INCIRCLE LINE IS BG'S OWN, not an NM leak. It
                                     reads as one because InCircle is NM's programme and
                                     the string is byte-identical to NM's `nm4` — but
                                     live BG runs it as slide 2 (offset 3520 in that
                                     capture, inside the ticker track, ahead of the
                                     masthead at 69px) and links it to
                                     `bg-incircle-popup-121825.html`. NMG runs one
                                     loyalty programme across both banners. What WAS
                                     wrong is that our copy truncated it: live ends
                                     "Learn More.", and slides 3 and 4 were missing
                                     entirely, so BG showed two of live's four. */
                                  {
                                      text: t('ticker.bg1', {
                                          defaultValue: 'Designer Sale: Extra 25% off for a limited time',
                                      }),
                                  },
                                  {
                                      text: t('ticker.bg2', {
                                          defaultValue: 'Free Standard shipping for InCircle members. Learn More.',
                                      }),
                                  },
                                  {
                                      text: t('ticker.bg3', {
                                          defaultValue: 'Get 15% off your first order in the BG app. Details >',
                                      }),
                                  },
                                  {
                                      text: t('ticker.bg4', {
                                          defaultValue: '4 interest-free payments with Klarna',
                                      }),
                                  },
                              ]
                            : [
                                  { text: t('ticker.nm1', { defaultValue: 'LAST CALL®: Extra 25% off sale styles' }) },
                                  { text: t('ticker.nm2', { defaultValue: 'New sale arrivals: up to 40% off' }) },
                                  {
                                      text: t('ticker.nm3', {
                                          defaultValue: 'Receive 15% off when you sign up for texts',
                                      }),
                                  },
                                  {
                                      text: t('ticker.nm4', {
                                          defaultValue: 'Free standard shipping for InCircle members. Learn more',
                                      }),
                                  },
                                  {
                                      text: t('ticker.nm5', {
                                          defaultValue: '15% off your first order in the app, new downloads only',
                                      }),
                                  },
                              ]
                    }
                />
            )}
            <NavContentProvider document={navDocument}>
                {/* Route-aware sticky header (blast-3: live's SRP header wrapper is
                    sticky, the homepage's is not — same per-page drift-matching
                    mechanism as the ticker band above).

                    BG IS NEVER STICKY, on any route, and that is a measurement
                    rather than a hold. Live BG's masthead scrolls away entirely
                    on search, PLP and PDP and nothing re-pins in its place: six
                    live-BG captures across two surfaces and five widths, both
                    scroll directions, show no header, no nav row and no ticker
                    pinned. The mechanism is why the static dumps and the
                    stylesheet never disagreed — live registers its `sticky-nav`
                    scroll listener behind a `LOG_DIR === 'bgo-ui-home'` gate,
                    and the commerce surfaces report `bgo-ui-plp`,
                    `bgo-ui-search` and `bgo-ui-pdp`, so the listener is never
                    attached there at all. It sits behind a second toggle that
                    defaults off. The sticky wrapper class live ships is an NM
                    fact: it occurs once across NM's dumps and zero times across
                    BG's.

                    THIS FLAG IS HALF A PAIR. The BG facet rail anchors its own
                    sticky offset to the header, so unsticking the header
                    without zeroing that offset parks the rail a header-height
                    below the viewport top on every scrolled BG search and PLP
                    page. The offset lives on `--facet-rail-sticky-offset` in
                    tokens/brand-bg/search.css; the two move together. */}
                <Header
                    sticky={brand === 'bg' ? false : !isHomeRoute}
                    mobileGenderTabs={isHomeRoute}
                    announcementSlot={
                        <EmbeddedComponentRegion component={refHeaderComponent.current} regionId="announcement" />
                    }
                    mobileMenuSlot={<CuratedMobileNav />}>
                    <CuratedDesktopNav />
                </Header>
            </NavContentProvider>
            {/* NO `pt-*` HERE ON PURPOSE. The top padding is
                theme/overrides/app-shell.css keyed off `data-has-top-padding`,
                which is what makes that attribute a working lever rather than
                the knob-nothing-turns it was until 2026-08-19. A utility class
                on this element outranks nothing and silently wins, which is
                exactly how the lever stayed dead while being documented as
                live. Default is "true" = 2rem = the `pt-8` this used to carry. */}
            <main className="grow" {...mainPaddingAttrs}>
                <Outlet />
            </main>
            <Footer />
        </>
    );
}
